import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Paper, Popper, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import useAuth from 'hooks/useAuth';
import { analytics } from 'lib/analytics';
import { PROMPTS, claimVisitSlot, isVisitSlotFree, whenPageSettled } from 'lib/prompts';
import { RADIUS } from 'components/ui/tokens';
import {
  ACTION_TARGETS,
  CLOSE_METHODS,
  HINT_ATTRIBUTE,
  HINT_EDGE_PX,
  HINT_FIND_ATTEMPTS,
  HINT_FIND_INTERVAL_MS,
  HINT_OFFSET_PX,
  HINT_WIDTH_PX,
  SURFACES
} from '../constants';
import { WHATS_NEW_COPY } from '../content';
import { useHintPosts, useMarkSeen } from '../hooks/useAnnouncements';
import type { CloseMethod, Post } from '../types';
import LabelPill from './LabelPill';

const c = WHATS_NEW_COPY.hint;
const TITLE_ID = 'whats-new-hint-title';
const SUMMARY_ID = 'whats-new-hint-summary';
const DESCRIBED_BY = 'aria-describedby';
const POPPER_MODIFIERS = [
  { name: 'offset', options: { offset: [0, HINT_OFFSET_PX] } },
  { name: 'flip', options: { padding: HINT_EDGE_PX } },
  { name: 'preventOverflow', options: { padding: HINT_EDGE_PX } }
];

const findTarget = (post: Post): HTMLElement | null => document.querySelector<HTMLElement>(`[${HINT_ATTRIBUTE}="${post.hintTarget}"]`);

/**
 * The one-time hint beside a new or improved control, once its element is on screen after the page settles. It
 * is the visit's one interruption, so it waits while a popup is due and never shows over anything else.
 */
const PageHint = ({ blocked }: { blocked: boolean }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const userId = user?.tcId;
  const { pathname } = useLocation();
  const posts = useHintPosts();
  // Keyed on the ids: a new array of the same posts must not restart the wait.
  const postsRef = useRef(posts);
  postsRef.current = posts;
  const postsKey = posts.map((post) => post.id).join();
  const markSeen = useMarkSeen();
  const [hint, setHint] = useState<{ post: Post; anchor: HTMLElement } | null>(null);
  const paper = useRef<HTMLDivElement>(null);

  // A new page or anything else taking the screen puts the hint away.
  useEffect(() => setHint(null), [pathname]);
  useEffect(() => {
    if (blocked) setHint(null);
  }, [blocked]);

  useEffect(() => {
    if (blocked || hint || !postsKey || !userId || !isVisitSlotFree(userId)) return undefined;
    let attempts = 0;
    let interval: number | undefined;
    let observer: IntersectionObserver | undefined;
    // True once there is nothing more to look for: found, or out of attempts.
    const look = (): boolean => {
      attempts += 1;
      const found = postsRef.current.map((post) => ({ post, anchor: findTarget(post) })).find((f) => f.anchor);
      if (!found?.anchor) return attempts >= HINT_FIND_ATTEMPTS;
      // A hint is the visit's one interruption, like the popup; something that took the visit meanwhile wins.
      const show = () => {
        if (claimVisitSlot(userId, PROMPTS.announcement)) setHint({ post: found.post, anchor: found.anchor as HTMLElement });
      };
      // Only once the element is actually in view; browsers without the observer just show it.
      if (typeof IntersectionObserver === 'undefined') {
        show();
        return true;
      }
      observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer?.disconnect();
        show();
      });
      observer.observe(found.anchor);
      return true;
    };
    const cancelWait = whenPageSettled(() => {
      if (look()) return;
      interval = window.setInterval(() => {
        if (look()) window.clearInterval(interval);
      }, HINT_FIND_INTERVAL_MS);
    });
    return () => {
      cancelWait();
      window.clearInterval(interval);
      observer?.disconnect();
    };
  }, [blocked, hint, postsKey, userId, pathname]);

  // Shown once, ever: the receipt is saved as soon as it appears.
  const shownId = hint?.post.id;
  useEffect(() => {
    if (!hint) return;
    markSeen([hint.post.id], SURFACES.hint);
    analytics.announcementShown({ post_id: hint.post.id, level: hint.post.level, surface: SURFACES.hint });
    // Keyed on the post: one showing, one receipt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownId]);

  // Screen readers hear the hint with its element, as the popper itself sits at the end of the page.
  useEffect(() => {
    if (!hint) return undefined;
    const { anchor } = hint;
    const before = anchor.getAttribute(DESCRIBED_BY);
    anchor.setAttribute(DESCRIBED_BY, [before, SUMMARY_ID].filter(Boolean).join(' '));
    return () => (before === null ? anchor.removeAttribute(DESCRIBED_BY) : anchor.setAttribute(DESCRIBED_BY, before));
  }, [hint]);

  const dismiss = (method: CloseMethod) => {
    if (!hint) return;
    analytics.announcementDismissed({ post_id: hint.post.id, surface: SURFACES.hint, method });
    // Keyboard users go back to the element rather than the top of the page.
    if (paper.current?.contains(document.activeElement)) hint.anchor.focus();
    setHint(null);
  };
  const tryIt = () => {
    if (!hint) return;
    analytics.announcementClicked({ post_id: hint.post.id, surface: SURFACES.hint, target: ACTION_TARGETS.cta });
    setHint(null);
    // The element may have re-rendered since the hint found it.
    (hint.anchor.isConnected ? hint.anchor : findTarget(hint.post))?.click();
  };

  useEffect(() => {
    if (!hint) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss(CLOSE_METHODS.esc);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownId]);

  if (!hint) return null;
  const { post } = hint;
  return (
    <Popper open anchorEl={hint.anchor} placement="bottom" modifiers={POPPER_MODIFIERS} sx={{ zIndex: theme.zIndex.appBar + 1 }}>
      <Paper
        ref={paper}
        role="dialog"
        aria-modal="false"
        aria-labelledby={TITLE_ID}
        aria-live="polite"
        sx={{
          width: HINT_WIDTH_PX,
          maxWidth: `calc(100vw - ${2 * HINT_EDGE_PX}px)`,
          p: 2,
          borderRadius: RADIUS.card,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
          boxShadow: `0 18px 40px -18px ${alpha(theme.palette.primary.main, 0.55)}`
        }}
      >
        <Stack direction="row" sx={{ mb: 0.75 }}>
          <LabelPill label={post.label} />
        </Stack>
        <Typography id={TITLE_ID} component="h2" sx={{ m: 0, fontSize: 14.5, fontWeight: 600, lineHeight: 1.35 }}>
          {post.title}
        </Typography>
        <Typography id={SUMMARY_ID} sx={{ mt: 0.5, fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>
          {post.summary}
        </Typography>
        <Stack direction="row" justifyContent="flex-end" spacing={0.5} sx={{ mt: 1.5 }}>
          <Button size="small" onClick={() => dismiss(CLOSE_METHODS.close)} sx={{ color: 'text.secondary' }}>
            {c.dismiss}
          </Button>
          <Button size="small" variant="contained" disableElevation onClick={tryIt}>
            {c.tryIt}
          </Button>
        </Stack>
      </Paper>
    </Popper>
  );
};

export default PageHint;
